import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Scale, ShieldCheck, FileText, Cookie, FileSignature, Lock, Download, Printer, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { LegalFooter } from "@/components/LegalFooter";
import { LEGAL_DOCS, LEGAL_UPDATED, legalDoc, legalPdfUrl } from "@/legal/content";
import { useAuth } from "@/context/AuthContext";

const ICONS = { privacy: ShieldCheck, terms: FileText, cookies: Cookie, security: Lock, dpa: FileSignature };

const Shell = ({ children, title }) => {
  const { user } = useAuth();
  useEffect(() => { document.title = `${title} — D20 Invoice AI`; window.scrollTo(0, 0); return () => { document.title = "D20 Invoice AI"; }; }, [title]);
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="legal-shell">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" aria-label="D20 home"><BrandLogo /></Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/legal" className="text-muted-foreground hover:text-foreground" data-testid="legal-nav-hub">Legal</Link>
            {user ? <Link to="/" className="text-primary hover:underline" data-testid="legal-nav-app">Back to app</Link> : <Link to="/login" className="text-primary hover:underline" data-testid="legal-nav-login">Sign in</Link>}
          </nav>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-4xl px-4 sm:px-6 py-8 sm:py-12">{children}</main>
      <LegalFooter />
    </div>
  );
};

export function LegalHubPage() {
  return (
    <Shell title="Legal">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground"><Scale className="h-3.5 w-3.5" /> Legal centre</div>
        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="legal-hub-title">Legal</h1>
        <p className="mt-3 text-base text-muted-foreground">The policies and agreements that govern D20 Invoice AI. Each document is its own page, so you can link to or print exactly what you need. Last updated {LEGAL_UPDATED}.</p>
      </div>
      <ul className="mt-8 divide-y divide-border rounded-xl border border-border bg-card" data-testid="legal-hub-list">
        {LEGAL_DOCS.map((d) => {
          const Icon = ICONS[d.slug];
          return (
            <li key={d.slug}>
              <Link to={`/legal/${d.slug}`} className="group flex items-start gap-4 p-4 sm:p-5 hover:bg-muted/50 transition-colors" data-testid={`legal-hub-${d.slug}`}>
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base sm:text-lg font-semibold group-hover:text-primary">{d.title}</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{d.summary}</span>
                </span>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
              </Link>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}

const Section = ({ s, idx }) => (
  <section className="mt-8" id={`s${idx}`}>
    <h2 className="text-base md:text-lg font-semibold">{s.h}</h2>
    {s.p?.map((t, i) => <p key={i} className="mt-2 text-sm sm:text-base leading-relaxed text-muted-foreground">{t}</p>)}
    {s.list && <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm sm:text-base leading-relaxed text-muted-foreground">{s.list.map((t, i) => <li key={i}>{t}</li>)}</ul>}
    {s.table && (
      <div className="mt-3 overflow-x-auto d20-scroll rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs text-muted-foreground"><tr>{s.table.head.map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{s.table.rows.map((r, i) => <tr key={i} className="border-t border-border align-top">{r.map((c, j) => <td key={j} className={j === 0 ? "px-3 py-2 font-medium whitespace-nowrap" : "px-3 py-2 text-muted-foreground"}>{c}</td>)}</tr>)}</tbody>
        </table>
      </div>
    )}
  </section>
);

const PdfButtons = ({ slug }) => (
  <div className="flex flex-wrap gap-2" data-testid="legal-pdf-actions">
    <a href={legalPdfUrl(slug, true)} data-testid="legal-download-pdf"
       className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
      <Download className="h-3.5 w-3.5" /> Download PDF
    </a>
    <a href={legalPdfUrl(slug)} target="_blank" rel="noreferrer" data-testid="legal-open-pdf"
       className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted">
      <Printer className="h-3.5 w-3.5" /> Open / print
    </a>
  </div>
);

export function LegalDocPage() {
  const { slug } = useParams();
  const doc = legalDoc(slug);
  const [body, setBody] = useState(null);
  useEffect(() => {
    if (!doc) return;
    setBody(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/legal/${slug}`).then((r) => setBody(r.data)).catch(() => setBody({ error: true }));
  }, [slug, doc]);
  if (!doc) return <Navigate to="/legal" replace />;
  const others = LEGAL_DOCS.filter((d) => d.slug !== slug);
  const sections = body?.sections || [];
  return (
    <Shell title={doc.title}>
      <Link to="/legal" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="legal-back"><ArrowLeft className="h-3.5 w-3.5" /> All legal documents</Link>
      <article className="mt-4" data-testid={`legal-doc-${slug}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="legal-doc-title">{doc.title}</h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="legal-doc-updated">Last updated {body?.updated || LEGAL_UPDATED}</p>
          </div>
          <PdfButtons slug={slug} />
        </div>
        {slug === "dpa" && <p className="mt-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="legal-dpa-sign-note">The PDF includes a signature block for both parties. Sign it and email it to {body?.contact || "privacy@d20invoice.com"} for a countersigned copy.</p>}
        <p className="mt-4 text-base text-muted-foreground">{doc.summary}</p>
        {!body && <div className="py-10 flex justify-center" data-testid="legal-doc-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {body?.error && <p className="mt-6 text-sm text-red-500" data-testid="legal-doc-error">We couldn't load this document right now. Please try again or download the PDF.</p>}
        {sections.length > 0 && (
          <nav className="mt-6 rounded-lg border border-border bg-card p-4 text-sm" aria-label="Contents" data-testid="legal-doc-contents">
            <div className="text-xs font-medium text-muted-foreground mb-2">On this page</div>
            <ol className="grid gap-1 sm:grid-cols-2">{sections.map((s, i) => <li key={s.h}><a href={`#s${i}`} className="hover:text-primary hover:underline">{s.h}</a></li>)}</ol>
          </nav>
        )}
        {sections.map((s, i) => <Section key={s.h} s={s} idx={i} />)}
        {sections.length > 0 && <div className="mt-10"><PdfButtons slug={slug} /></div>}
      </article>
      <div className="mt-12 border-t border-border pt-6">
        <div className="text-xs font-medium text-muted-foreground mb-2">Other documents</div>
        <div className="flex flex-wrap gap-2">{others.map((d) => <Link key={d.slug} to={`/legal/${d.slug}`} className="rounded-full border border-border px-3 py-1 text-sm hover:border-primary hover:text-primary" data-testid={`legal-other-${d.slug}`}>{d.title}</Link>)}</div>
      </div>
    </Shell>
  );
}
